import Input from "../../components/Input/Input"
import React, { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { showSnackBar } from "../../components/Snackbar/Snackbar"
import Button from "../../components/Button/Button"
import { NavLink, useNavigate } from "react-router-dom"
import { areFieldsNotEmpty } from "../../helpers/helper"
import { phoneNumber } from "../../utils/payment"
import { useMyContextLoading } from "../../components/LoadingContextProvider/LoadingContextProvider"
import ContainerPage from "../../components/ContainerPage/ContainerPage"
import PageTitle from "../../components/PageTitle/PageTitle"
import Calendar from "../../components/Calendar/Calendar"
import { serverToLocalDateOnly } from "../../utils/date"
import { getAffiliateById, updateAffiliate } from "../../features/affiliates/affiliates"
import { clearListCache } from "../../utils/listCache"
import Dropdown from "../../components/Dropdown/Dropdown"
import { fetchContactGroupsDropdownInfo } from "../../features/contactGroups/contactGroups"
import { StorageGetItem, USER } from "../../lib/storage"
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal"
import useAffiliateStatusChange from "./useAffiliateStatusChange"
import { AFFILIATE_STATUS_OPTIONS, statusConfirmTitle, statusConfirmMessage } from "./affiliateStatus"
import BrandAccessPanel from "../../components/BrandAccess/BrandAccessPanel"
import EmailSettingsPanel from "../../components/EmailSettings/EmailSettingsPanel"
import AccountingDailyEmailSwitch from "../../components/AccountingDailyEmailSwitch/AccountingDailyEmailSwitch"
import { selectFeatureFlag } from "../../features/featureFlags/selectors"
import { AFFILIATE_GROUP_CHANGE_PREVIEW, PAYMENTS_AGENT_CARD_OVERRIDE } from "../../config/featureFlagKeys"
import useAffiliateGroupChangeConfirm from "./useAffiliateGroupChangeConfirm"
import { GROUP_CHANGE_TITLE, groupsChanged } from "./affiliateGroupChange"

const EditAffiliates = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { triggerLoading } = useMyContextLoading()
  const currentURL = window.location.href
  const parts = currentURL.split('/')
  const userId = parts[parts.length - 1]

  const [reqError, setReqError] = useState('')
  const [contactGroups, setContactGroups] = useState([])
  const [userRole, setUserRole] = useState('')
  const affiliateManagerOptions = [
    { value: true, label: 'Yes' },
    { value: false, label: 'No' }
  ]
  const cardOverrideOptions = [
    { value: true, label: 'Allowed' },
    { value: false, label: 'Not allowed' }
  ]
  const accessLevelOptions = [
    { value: 'limited', label: 'Limited (Group Only)' },
    { value: 'fullContactsNoAccounting', label: 'Full Contacts' }
  ]
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    affiliateManager: null,
    accessLevel: 'limited',
    contactGroups: [],
  })
  const [isFormValid, setIsFormValid] = useState(false)
  const [homeCompany, setHomeCompany] = useState('')
  // Access status lives OUTSIDE formData: the Save/PUT endpoint drops affiliateStatus,
  // so it must only ever change through the dedicated PATCH flow below.
  const [affiliateStatus, setAffiliateStatus] = useState('active')
  const statusChange = useAffiliateStatusChange({
    onSuccess: (updated) => setAffiliateStatus(updated?.affiliateStatus || 'active'),
  })

  // Flags load after the app mounts, so a deep link would read OFF at first: latch ON once seen.
  const livePreview = useSelector(selectFeatureFlag(AFFILIATE_GROUP_CHANGE_PREVIEW))
  const previewOnRef = useRef(false)
  if (livePreview) previewOnRef.current = true
  const previewOn = previewOnRef.current

  // WS #2515680. The server takes this field from internal staff only and only while the flag is on,
  // so the screen offers it, and sends it, in exactly those cases.
  const cardOverrideOn = useSelector(selectFeatureFlag(PAYMENTS_AGENT_CARD_OVERRIDE))
  const [allowCardWhenOfficeBankOnly, setAllowCardWhenOfficeBankOnly] = useState(false)
  const showCardOverride = cardOverrideOn && userRole === 'customer'

  // The membership as loaded, to tell a group change from any other save.
  const initialGroupsRef = useRef([])
  const groupChange = useAffiliateGroupChangeConfirm({
    userId,
    name: `${formData?.firstName || ''} ${formData?.lastName || ''}`.trim(),
    onConfirm: () => handleEditAffiliates(),
  })

  useEffect(() => {
    const handleReqError = () => {
      reqError.length > 0 && showSnackBar("error", reqError)
      setReqError('')
    }

    handleReqError()
  }, [reqError])

  const handleEditAffiliates = async () => {
    let newNumber = formData?.primaryPhone
    triggerLoading(true)
    if (newNumber) {
      newNumber = formData.primaryPhone?.replace(/\D/g, "")
    }

    const response = await dispatch(updateAffiliate({
      userData: {
        ...formData,
        primaryPhone: newNumber,
        ...(showCardOverride ? { allowCardWhenOfficeBankOnly } : {}),
      },
      userId
    }))
    if (response.error) {
      setReqError(response.payload)
    } else if (response) {
      showSnackBar("success", "Your changes are saved")
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        affiliateManager: null,
        accessLevel: 'limited',
      })
      clearListCache('affiliates')
      navigate('/affiliates')
    }
    triggerLoading(false)
  }

  const handleSave = () => (
    previewOn && groupsChanged(initialGroupsRef.current, formData?.contactGroups)
      ? groupChange.requestSave(formData?.contactGroups || [])
      : handleEditAffiliates()
  )

  const getContactGroupsDropdownInfo = async () => {
    triggerLoading(true)
    const response = await dispatch(fetchContactGroupsDropdownInfo())
    if (response.error) {
      showSnackBar("error", response?.payload || "Something went wrong")
    } else {
      setContactGroups(response?.payload?.data?.filter(group => group.isDepartment !== true))
    }
    triggerLoading(false)
  }

  const handleInputChange = ({ name, value }) => {
    switch (name) {
      case 'primaryPhone':
        setFormData((prevData) => ({ ...prevData, [name]: phoneNumber(value) }))
        break
      default:
        const [parentField, childField] = name.split('.')
        if (childField) {
          setFormData((prevData) => ({
            ...prevData,
            [parentField]: {
              ...prevData[parentField],
              [childField]: value
            }
          }))
        } else {
          setFormData((prevData) => ({
            ...prevData,
            [name]: value
          }))
        }
        break
    }
  }

  const getAffiliatesByIdReq = async () => {
    triggerLoading(true)
    try {
      const response = await dispatch(getAffiliateById(userId))
      setHomeCompany(response?.payload?.data?.company || '')
      initialGroupsRef.current = (response?.payload?.data?.contactGroups || []).map(group => group?._id || group)
      setFormData({
        firstName: response?.payload?.data?.firstName || '',
        lastName: response?.payload?.data?.lastName || '',
        email: response?.payload?.data?.email || '',
        primaryPhone: phoneNumber(response?.payload?.data?.primaryPhone) || '',
        address: {
          address: response?.payload?.data?.address?.address || '',
          aptNumber: response?.payload?.data?.address?.aptNumber || '',
          city: response?.payload?.data?.address?.city || '',
          state: response?.payload?.data?.address?.state || '',
          zip: response?.payload?.data?.address?.zip || '',
        },
        dateOfBirth: serverToLocalDateOnly(response?.payload?.data?.dateOfBirth) || '',
        contactGroups: (response?.payload?.data?.contactGroups || []).map(group => group?._id || group),
        affiliateManager: response?.payload?.data?.affiliateManager ?? null,
        accessLevel: response?.payload?.data?.accessLevel || 'limited',
      })
      setAffiliateStatus(response?.payload?.data?.affiliateStatus || 'active')
      setAllowCardWhenOfficeBankOnly(response?.payload?.data?.allowCardWhenOfficeBankOnly === true)
    } catch (error) {
      console.error(error)
      setReqError(error.message || 'Failed to get affiliate data')
    }
    triggerLoading(false)
  }

  useEffect(() => {
    const checkFormValidity = () => {
      setIsFormValid(
        areFieldsNotEmpty([formData.firstName, formData.lastName, formData.email]) &&
        formData.dateOfBirth !== ""
      )
    }

    checkFormValidity()
  }, [formData])

  useEffect(() => {
    StorageGetItem(USER).then(result => {
      setUserRole(result?.role)
    })
  }, [])

  useEffect(() => {
    getAffiliatesByIdReq()
    getContactGroupsDropdownInfo()
  }, [])

  return (
    <ContainerPage>
      <div className="flex justify-between items-center">
        <PageTitle title="Edit affiliate" backTo="/affiliates"
          subtitle={[formData?.firstName, formData?.lastName].filter(Boolean).join(' ')} />
      </div>
      <div className="flex w-full items-center justify-center">
        <div className="w-full max-w-[850px] rounded-2xl bg-surface border border-divider shadow-card p-12">
          <div className="grid grid-cols-2 gap-x-4 gap-y-6 pb-6 h-max border-b border-divider ">
            <Input
              width="w-full"
              placeholder="Enter First Name"
              label="First name *"
              value={formData.firstName}
              onChange={(e) => handleInputChange({ name: "firstName", value: e.target.value })}
            />
            <Input
              width="w-full"
              placeholder="Enter Last Name"
              label="Last name *"
              value={formData.lastName}
              onChange={(e) => handleInputChange({ name: "lastName", value: e.target.value })}
            />
            <Input
              type="email"
              width="w-full"
              placeholder="Enter Email"
              label="Email *"
              value={formData.email}
              onChange={(e) => handleInputChange({ name: "email", value: e.target.value })}
            />
            <Input
              width="w-full"
              placeholder="Enter Primary Phone"
              label="Primary Phone"
              value={formData.primaryPhone}
              onChange={(e) => handleInputChange({ name: "primaryPhone", value: e.target.value })}
            />
            <Input
              width="w-full"
              placeholder="Enter Address"
              label="Address"
              value={formData?.address?.address}
              onChange={(e) => handleInputChange({ name: "address.address", value: e.target.value })}
            />
            <Input
              width="w-full"
              placeholder="34"
              label="Suite/Apt#"
              value={formData?.address?.aptNumber}
              onChange={(e) => handleInputChange({ name: "address.aptNumber", value: e.target.value })}
            />
            <Input
              width="w-full"
              placeholder="Enter City"
              label="City"
              value={formData?.address?.city}
              onChange={(e) => handleInputChange({ name: "address.city", value: e.target.value })}
            />
            <Input
              width="w-full"
              placeholder="Enter State"
              label="State"
              value={formData?.address?.state}
              onChange={(e) => handleInputChange({ name: "address.state", value: e.target.value })}
            />
            <Input
              type="number"
              width="w-full"
              placeholder="Enter Zip"
              label="Zip"
              value={formData?.address?.zip}
              onChange={(e) => handleInputChange({ name: "address.zip", value: e.target.value })}
            />
            <Calendar
              width="w-full"
              label="Date Of Birth *"
              height="56px"
              value={formData.dateOfBirth}
              onChange={(e) => handleInputChange({ name: "dateOfBirth", value: e })}
            />
            <Dropdown
              value={formData?.contactGroups || []}
              setValue={(e) => handleInputChange({ name: "contactGroups", value: e })}
              menuItems={contactGroups}
              isMulti
              className="w-full mt-2"
              variant="standard"
              placeholder="Group"
              label="Contact Groups"
            />
            <Dropdown
              value={affiliateStatus}
              setValue={(e) => statusChange.requestChange({ userId, status: e, currentStatus: affiliateStatus })}
              menuItems={AFFILIATE_STATUS_OPTIONS}
              className="w-full mt-2"
              variant="standard"
              placeholder="Select status"
              label="Status"
              disabled={statusChange.busy}
            />
            {userRole === 'customer' && (
              <Dropdown
                value={formData?.affiliateManager}
                setValue={(e) => handleInputChange({ name: "affiliateManager", value: e })}
                menuItems={affiliateManagerOptions}
                className="w-full mt-2"
                variant="standard"
                placeholder="Select option"
                label="Senior Affiliate"
              />
            )}
            {userRole === 'customer' && (
              <Dropdown
                value={formData?.accessLevel}
                setValue={(e) => handleInputChange({ name: "accessLevel", value: e })}
                menuItems={accessLevelOptions}
                className="w-full mt-2"
                variant="standard"
                placeholder="Select access level"
                label="Access Level"
              />
            )}
            {showCardOverride && (
              <Dropdown
                value={allowCardWhenOfficeBankOnly}
                setValue={(e) => setAllowCardWhenOfficeBankOnly(e === true)}
                menuItems={cardOverrideOptions}
                className="w-full mt-2"
                variant="standard"
                placeholder="Select option"
                label="Cards in a bank-only office"
              />
            )}
            {userRole === 'customer' && <AccountingDailyEmailSwitch userId={userId} />}
          </div>
          <div className={`mt-6 flex justify-between items-center`}>
            <Button
              onClick={() => {
                const email = encodeURIComponent(formData.email || '')
                navigate(`/admin/reset-password?userId=${userId}&userType=affiliate&email=${email}`)
              }}
              type="outlined"
              size="lg"
              width="min-w-[156px]"
            >
              Reset Password
            </Button>
            <div className="flex space-x-4">
              <NavLink to={"/affiliates"}>
                <Button classes={"mr-4"} onClick={() => {
                }} type="outlined" size="lg" width="min-w-[156px]">Close</Button>
              </NavLink>
              <Button
                onClick={handleSave}
                size="lg"
                width="min-w-[156px]"
                disabled={!isFormValid || groupChange.busy}
                classes="rounded-lg"
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div className="flex w-full items-center justify-center">
        <div className="w-full max-w-[850px]">
          <BrandAccessPanel userId={userId} homeCompany={homeCompany} />
          <EmailSettingsPanel userId={userId} />
        </div>
      </div>
      {statusChange.pending && (
        <ConfirmModal
          title={statusConfirmTitle(statusChange.targetStatus)}
          modalButtonTitle="Confirm"
          open={statusChange.pending}
          setOpen={(val) => !val && statusChange.cancel()}
          functionButton={statusChange.confirm}
        >
          {statusConfirmMessage({ status: statusChange.targetStatus, isOwner: statusChange.isOwner })}
        </ConfirmModal>
      )}
      {groupChange.pending && (
        <ConfirmModal
          title={GROUP_CHANGE_TITLE}
          modalButtonTitle="Save"
          open={groupChange.pending}
          setOpen={(val) => !val && groupChange.cancel()}
          functionButton={groupChange.confirm}
        >
          {groupChange.message}
        </ConfirmModal>
      )}
    </ContainerPage>
  )
}

export default EditAffiliates
